import React from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import Icon from '../icon/Icon'
import MarkdownToolbarButton from './MarkdownToolbarButton'

// Types
import { BlockMark, InlineMark } from '../../utils/markdown'

export interface IMarkdownActiveMarks {
  bold: boolean
  italic: boolean
  heading: boolean
  bullet: boolean
  numbered: boolean
}

interface IProps {
  activeMarks: IMarkdownActiveMarks
  preview: boolean
  showHeading?: boolean
  // Sits under the field instead of above it; only the divider side changes.
  bottom?: boolean
  onInline: (mark: InlineMark) => void
  onBlock: (mark: BlockMark) => void
  onTogglePreview: () => void
}

// Formatting toolbar shown next to a markdown-enabled Textarea. Renders toggle
// buttons for inline (bold/italic) and block (heading/bullet/numbered) marks
// plus a write/preview switch. Stateless — active state and handlers come from
// the parent Textarea; the heading button is hidden unless showHeading is set.
const MarkdownToolbar = ({
  activeMarks,
  preview,
  showHeading,
  bottom,
  onInline,
  onBlock,
  onTogglePreview,
}: IProps) => {
  const { t } = useTranslation()
  return (
    <Toolbar $bottom={bottom}>
      <Group>
        <MarkdownToolbarButton
          label={t('markdown.toolbar.bold')}
          active={activeMarks.bold}
          disabled={preview}
          onTrigger={() => onInline('bold')}
        >
          <Icon type="Bold" size={16} color="currentColor" />
        </MarkdownToolbarButton>
        <MarkdownToolbarButton
          label={t('markdown.toolbar.italic')}
          active={activeMarks.italic}
          disabled={preview}
          onTrigger={() => onInline('italic')}
        >
          <Icon type="Italic" size={16} color="currentColor" />
        </MarkdownToolbarButton>
        {showHeading && (
          <MarkdownToolbarButton
            label={t('markdown.toolbar.heading')}
            active={activeMarks.heading}
            disabled={preview}
            onTrigger={() => onBlock('heading')}
          >
            <Icon type="Heading" size={16} color="currentColor" />
          </MarkdownToolbarButton>
        )}
        <MarkdownToolbarButton
          label={t('markdown.toolbar.bulletList')}
          active={activeMarks.bullet}
          disabled={preview}
          onTrigger={() => onBlock('bullet')}
        >
          <Icon type="BulletList" size={16} color="currentColor" />
        </MarkdownToolbarButton>
        <MarkdownToolbarButton
          label={t('markdown.toolbar.numberedList')}
          active={activeMarks.numbered}
          disabled={preview}
          onTrigger={() => onBlock('numbered')}
        >
          <Icon type="NumberedList" size={16} color="currentColor" />
        </MarkdownToolbarButton>
      </Group>
      <MarkdownToolbarButton
        label={
          preview ? t('markdown.toolbar.write') : t('markdown.toolbar.preview')
        }
        active={preview}
        onTrigger={onTogglePreview}
      >
        <Icon type="Show" size={16} color="currentColor" />
      </MarkdownToolbarButton>
    </Toolbar>
  )
}

const Toolbar = styled.div<{ $bottom?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px;
  border-${({ $bottom }) => ($bottom ? 'top' : 'bottom')}: 1px solid
    ${({ theme }) => theme.colors.textLightest};
`

const Group = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

export default MarkdownToolbar
